/**
 * Pure logic for the subject's ask-card — the client-side half of the backend's
 * rules for `GET /v1/hits/{id}/preview` and `POST /v1/hits/{id}/decision`.
 *
 * WHAT LIVES HERE AND WHY. The api authors the question itself
 * (`src/reports/card.ts`) and this file never derives one: two of the five
 * severities mean the OPPOSITE of a match — `likely_not_subject` is a
 * face-match that ran and failed, `unassessed` is no check at all — and a
 * client that composed its own sentence could claim a match the system never
 * made. So `question` is rendered verbatim. What is left for this file is the
 * decisions a browser has to make on its own:
 *
 *  - WHEN A CROP MAY BE FETCHED AT ALL (`shouldLoadCrop`). Services invariant
 *    #23: a blurred crop renders only on the hit's own card, never in a list
 *    view, digest, email or push; an un-blurred one only after that user's
 *    explicit per-item tap. A predicate makes that assertable, and it is also
 *    the render budget — their ceiling is 200 per rolling 24 hours and counts
 *    ATTEMPTS, so a list that eagerly thumbnailed ten hits would spend a tenth
 *    of somebody's day of previews on one scroll.
 *  - HOW THE THREE NON-IMAGE OUTCOMES READ (`previewNote`). None of them is an
 *    error state; two of them are ordinary.
 */

/** The four cards the api serves, from `svc.v_person_hits.confirm_state`. */
export type HitCardKind = 'being_checked' | 'ask' | 'finding' | 'dismissed';

/** Cards whose own detail view may show pixels at all. */
const CROP_BEARING_CARDS: readonly string[] = ['ask', 'finding'];

export interface CropGate {
  card: string;
  /** The api's advisory: false means the preview 404s `PREVIEW_UNAVAILABLE`. */
  previewExpected: boolean;
  /** Whether THIS hit's own card is the one the user has open. */
  isOpen: boolean;
}

/**
 * Whether to fetch a crop for this hit, right now.
 *
 * Three conditions, all required, and the closed card is the important one: it
 * is what keeps a list of ask-able hits at zero renders. A card value we do not
 * recognise returns false — a card the api adds later does not inherit
 * permission to show somebody their own abuse material.
 */
export function shouldLoadCrop(gate: CropGate): boolean {
  if (!gate.isOpen) return false;
  if (!gate.previewExpected) return false;
  return CROP_BEARING_CARDS.includes(gate.card);
}

/**
 * The badge on a hit row. NOT a verdict: "being checked" must not read as "we
 * found you", and nothing here may read as "you're safe" (services #26).
 */
const CARD_BADGE: Record<string, string> = {
  ask: 'is this you?',
  being_checked: 'being checked',
  finding: 'confirmed by you',
  dismissed: 'dismissed',
};

export function cardBadge(card: string): string {
  // An unknown card reads as the most cautious of the four — it claims nothing
  // and offers nothing.
  return CARD_BADGE[card] ?? 'being checked';
}

/**
 * The three responses that are not an image, in the user's terms.
 *
 * `PREVIEW_UNAVAILABLE` IS NOT AN ERROR. It is the ordinary answer for a hit
 * that has not been checked yet and for one where no face was found, and the
 * card stays answerable without it — so the line says that rather than
 * apologising. `RATE_LIMITED` sends them away for now and deliberately does not
 * offer a retry: the ceiling is a rolling 24-hour window and hammering it just
 * spends what is left. `DECISION_CONFLICT` has no retry either — there is no
 * re-decide in v1.
 *
 * An unrecognised code returns null so the screen falls back to the api's own
 * envelope message, which is this client's whole posture: never hide the one
 * thing it exists to show.
 */
const PREVIEW_NOTE: Record<string, string> = {
  PREVIEW_UNAVAILABLE:
    'No picture to show for this one — you can still answer from the site and the date.',
  RATE_LIMITED: 'You have looked at a lot of pictures today. Please come back later.',
  DECISION_CONFLICT: 'This has already been decided. Contact support if you need it changed.',
};

export function previewNote(code: string): string | null {
  return PREVIEW_NOTE[code] ?? null;
}

export interface AnswerOption {
  decision: 'confirmed' | 'rejected';
  label: string;
  /** The plain meaning, so neither button is a guess. */
  meaning: string;
}

/**
 * The two answers, and there is no third.
 *
 * "Not sure" is NOT a decision: an unsure subject leaves the hit undecided and
 * answers later, which is why the card says so in words instead of offering a
 * button that would have to mean one of these two.
 */
export function answerLabels(): AnswerOption[] {
  return [
    { decision: 'confirmed', label: 'Yes, that is me', meaning: 'this photo is of me' },
    { decision: 'rejected', label: 'That is not me', meaning: 'this is somebody else' },
  ];
}

/**
 * THE PREVIEW'S OWN WORDS, in one place because they are safety claims.
 *
 * Since 2026-09-02 the preview is the WHOLE hit frame, blurred end to end, and
 * the tap sharpens ONLY the face box. Services expose no parameter that returns
 * a fully sharp frame — the explicit content of a hit is never rendered sharp
 * by this system.
 *
 * So a control labelled "show me the picture" describes something that cannot
 * happen, and it errs in the direction that hurts: it tells someone we are
 * about to show them their abuse image in full. The label names the face,
 * because the face is the whole of what sharpens.
 */
export const PREVIEW_COPY = {
  /** The reveal control. Never promises the picture. */
  reveal: 'Sharpen the face',
  /** Under the blurred frame, before the tap. */
  blurredNote: 'Blurred until you choose to look. Only the face will sharpen.',
  /** Under the frame once sharpened — the rest of it stays hidden. */
  revealedNote: 'The rest of the picture stays blurred.',
  altBlurred: 'The photo we found, blurred.',
  altRevealed: 'The photo we found, blurred, with the face sharpened.',
} as const;

/** Shown beside the two buttons. Leaving a hit unanswered has to be allowed. */
export const NOT_SURE_NOTE =
  'Not sure? Leave it — nothing happens until you answer, and you can come back to it.';
